import React, { useState, useEffect } from 'react';
import {
  Button,
  ButtonDropdown,
  ContentLayout,
  Header,
  Pagination,
  Table,
  TextFilter,
  SpaceBetween,
  StatusIndicator,
  Link,
  Box
} from '@cloudscape-design/components';

import { listProjects } from '../../repositories/listProjects'

export function JobsPage({ jwtToken }) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [projects, setProjects] = useState([]); // Add state for projects
  const [loading, setLoading] = useState(true); // Add loading state
  const [error, setError] = useState(null); // Add error state for handling errors

  // Fetch projects when component mounts
  useEffect(() => {
    fetchProjects();
  }, []);

  // Function to fetch projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const fetchedProjects = await listProjects(jwtToken);
      console.log(fetchedProjects )
      setProjects(fetchedProjects);
      setError(null);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };
  
  // Column definitions for the table
  const columnDefinitions = [
    {
      id: 'name',
      header: 'Project name',
      cell: item => (
        <Link href="#">{item.name}</Link>
      ),
      sortingField: 'name'
    },
    {
      id: 'projectId',
      header: 'Project ID',
      cell: item => item.projectId,
      sortingField: 'projectId'
    },
    {
      id: 'status',
      header: 'Project status',
      cell: item => (
        <StatusIndicator type={item.statusType}>
          {item.status}
        </StatusIndicator>
      ),
      sortingField: 'status'
    },
    {
      id: 'phase',
      header: 'Current phase',
      cell: item => item.phase,
      sortingField: 'phase'
    },
    {
      id: 'lastUpdated',
      header: 'Last updated',
      cell: item => item.lastUpdated
    },
    {
      id: 'alarmStatus',
      header: 'Alarm status',
      cell: item => item.alarmStatus
    },
    {
      id: 'team',
      header: 'Team',
      cell: item => item.team,
      sortingField: 'team'
    }
  ];

  // Dummy data
  const projects1 = [
    {
      name: 'ECommerce Modernization',
      projectId: 'proj-1234',
      status: 'In progress',
      statusType: 'in-progress',
      phase: 'Technical Analysis',
      lastUpdated: '2024-04-24 14:30',
      alarmStatus: 'No alarms',
      team: 'Team Alpha'
    },
    {
      name: 'Banking Portal Legacy Migration',
      projectId: 'proj-2345',
      status: 'Running',
      statusType: 'success',
      phase: 'Domain Analysis',
      lastUpdated: '2025-04-24 16:30',
      alarmStatus: '1 alarm',
      team: 'Team Beta'
    },
    {
      name: 'CRM System Decomposition',
      projectId: 'proj-3456',
      status: 'Failed',
      statusType: 'error',
      phase: 'Microservice Generation',
      lastUpdated: '2025-01-14 14:00',
      alarmStatus: '3 alarms',
      team: 'Team Gamma'
    },
    {
      name: 'HR Management System',
      projectId: 'proj-4567',
      status: 'Pending',
      statusType: 'pending',
      phase: 'Not Started',
      lastUpdated: '2025-03-27 10:30',
      alarmStatus: 'No alarms',
      team: 'Team Delta'
    },
    {
      name: 'Inventory Management Modernization',
      projectId: 'proj-5678',
      status: 'Completed',
      statusType: 'success',
      phase: 'Completed',
      lastUpdated: '2024-04-24 14:30',
      alarmStatus: 'No alarms',
      team: 'Team Alpha'
    },
    {
      name: 'Payment Gateway Migration',
      projectId: 'proj-6789',
      status: 'In progress',
      statusType: 'in-progress',
      phase: 'Domain Analysis',
      lastUpdated: '2025-04-25 09:30',
      alarmStatus: 'No alarms',
      team: 'Team Beta'
    },
    {
      name: 'Customer Support Portal',
      projectId: 'proj-7890',
      status: 'Paused',
      statusType: 'stopped',
      phase: 'Technical Analysis',
      lastUpdated: '2024-04-24 14:30',
      alarmStatus: '1 alarm',
      team: 'Team Gamma'
    },
    {
      name: 'Order Processing System',
      projectId: 'proj-8901',
      status: 'In progress',
      statusType: 'in-progress',
      phase: 'Microservice Generation',
      lastUpdated: '2024-09-24 17:30',
      alarmStatus: 'No alarms',
      team: 'Team Alpha'
    },
    {
      name: 'Legacy Reporting System',
      projectId: 'proj-9012',
      status: 'Planning',
      statusType: 'pending',
      phase: 'Not Started',
      lastUpdated: '2023-10-24 14:30',
      alarmStatus: 'No alarms',
      team: 'Team Delta'
    },
    {
      name: 'Authentication Service Migration',
      projectId: 'proj-0123',
      status: 'In review',
      statusType: 'warning',
      phase: 'Domain Analysis',
      lastUpdated: '2024-04-24 14:30',
      alarmStatus: '2 alarms',
      team: 'Team Beta'
    },
    {
      name: 'Data Warehouse Modernization',
      projectId: 'proj-1122',
      status: 'Running',
      statusType: 'success',
      phase: 'Technical Analysis',
      lastUpdated: '2025-03-04 12:30',
      alarmStatus: 'No alarms',
      team: 'Team Gamma'
    },
    {
      name: 'Mobile App Backend',
      projectId: 'proj-2233',
      status: 'Failed',
      statusType: 'error',
      phase: 'Microservice Generation',
      lastUpdated: '2024-04-24 14:30',
      alarmStatus: '4 alarms',
      team: 'Team Alpha'
    }
  ];

  // Empty state
  const empty = (
    <Box textAlign="center" color="inherit">
      <Box variant="strong" textAlign="center" color="inherit">
        No projects
      </Box>
      <Box padding={{ bottom: 's' }} variant="p" color="inherit">
        You don't have any modernization projects in this region.
      </Box>
      <Button>Create project</Button>
    </Box>
  );

  return (
    <ContentLayout
      header={
        <SpaceBetween size="m">
          <Header
            variant="h1"
            info={<Link>Info</Link>}
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <ButtonDropdown
                  items={[
                    { text: 'Start', id: 'start' },
                    { text: 'Stop', id: 'stop' },
                    { text: 'Restart', id: 'restart' }
                  ]}
                >
                  Project state
                </ButtonDropdown>
                <ButtonDropdown
                  items={[
                    { text: 'View details', id: 'details' },
                    { text: 'Edit', id: 'edit' },
                    { text: 'Delete', id: 'delete' }
                  ]}
                >
                  Actions
                </ButtonDropdown>
                <Button variant="primary">Create project</Button>
              </SpaceBetween>
            }
          >
            Projects
          </Header>

          <Table
            columnDefinitions={columnDefinitions}
            items={projects}
            loading={loading}
            loadingText="Loading projects"
            selectionType="multi"
            selectedItems={selectedItems}
            onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
            empty={empty}
            header={
              <Header
                counter={`(${selectedItems.length}/${projects.length})`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <ButtonDropdown
                      items={[
                        { text: 'Bundle and image', id: 'bundle' },
                        { text: 'Instance state', id: 'state' },
                        { text: 'Security', id: 'security' },
                        { text: 'Manage tags', id: 'tags' }
                      ]}
                    >
                      Actions
                    </ButtonDropdown>
                    <Button iconName="refresh" 
                    onClick={fetchProjects}
                    loading={loading} />
                    <Button iconName="settings" />
                  </SpaceBetween>
                }
              >
                Projects
              </Header>
            }
            filter={
              <TextFilter
                filteringPlaceholder="Find projects by attribute or tag"
                filteringText=""
              />
            }
            pagination={
              <Pagination
                currentPageIndex={1}
                pagesCount={1}
              />
            }
            preferences={
              <Button
                iconName="settings"
                variant="icon"
                onClick={() => {}}
              />
            }
          />
        </SpaceBetween>
      }
    />
  );
};
